import { Trans } from 'react-i18next'
import { Descendant } from 'slate'

import { IActionFormProps, VARIABLE_TEXT_FEATURE_FLAG_ALL, VariableTextFeatureFlag } from '@makyoapp/makyo-common'
import { Button, ButtonVariant, Field, Label } from '@makyoapp/makyo-ui'

import { getDefaultDescendantValue } from '@/lib/slate'
import { useForm } from '@/lib/use-form'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useGlobalVariables } from '@/hooks/useGlobalVariables'
import { getDataSourceKeyForComponent } from '@/shared/builder/utils'
import GlobalVariablesDropdown from '@/components/GlobalVariablesDropdown'
import { VariableTextV2 } from '@/components/VariableTextV2/VariableTextV2'

// const scopeOptions = [
//   { value: 'session', label: 'Session' },
//   { value: 'persistent', label: 'Persistent' },
// ]

export default function ActFormSetGlobalVariable({
  onSubmit,
  onDismiss,
  initialData,
  triggerSource,
}: IActionFormProps) {
  const { form, onSubmit: onFormSubmit } = useForm({
    defaultValues: {
      name: initialData?.name || '',
      value: initialData?.value || getDefaultDescendantValue(''),
      scope: initialData?.scope || 'persistent',
    },
  })

  const handleSubmit = onFormSubmit((data) => {
    onSubmit({ ...data })
  })

  const { activeLayerContainer } = useActiveLayerContext()
  const { activeComponent } = useActiveLayerContext()
  const dataSourceKey = getDataSourceKeyForComponent(activeComponent?.compId || '')
  const table = dataSourceKey
    ? (activeLayerContainer?.elementSettings?.[activeComponent?.id as string]?.[dataSourceKey] ?? null)
    : null

  const activeTableId = (table as any)?.table ?? table

  const invalidForm = !form.getValues('name')

  let featureFlags =
    (VARIABLE_TEXT_FEATURE_FLAG_ALL &
      ~VariableTextFeatureFlag.Icon &
      ~VariableTextFeatureFlag.Image &
      ~VariableTextFeatureFlag.Column &
      ~VariableTextFeatureFlag.ColumnData) |
    VariableTextFeatureFlag.DateTime |
    VariableTextFeatureFlag.ActionOutput

  if (triggerSource === 'newsletter') {
    featureFlags = featureFlags | VariableTextFeatureFlag.NewsletterEmail
  }

  const value = form.getValues('name')
  const currentValue = typeof value === 'object' ? value.value : value

  return (
    <div className='w-full max-w-[768px]'>
      <div className='p-4'>
        {/* Description */}
        <p className='text-gray-300 mb-6 leading-relaxed'>
          <Trans>
            Global Variables let you pass data between screens and keep information accessible as users navigate your
            app. On this screen, you can set a Global Variable by choosing an existing one or creating a new one. If the
            variable already has a value, it will be overwritten.
          </Trans>
        </p>

        {/* Form Content */}
        <div className='flex-grow overflow-y-auto pr-2 space-y-6 '>
          {/* Name Field */}
          <Field>
            <Label required>Select/Add New</Label>

            <GlobalVariablesDropdown
              editable
              value={currentValue}
              onChange={(value) => {
                form.setValues('name', value)
              }}
            />
          </Field>

          {/* Value Field */}
          <Field>
            <Label required>Value</Label>

            <VariableTextV2
              tableId={activeTableId}
              featureFlags={
                initialData?.isActionWorkflow ? featureFlags : featureFlags & ~VariableTextFeatureFlag.ActionOutput
              }
              value={form.getValues('value') as Descendant[]}
              onChange={(value) => {
                form.setValues('value', value)
              }}
              placeholder='Enter value'
              options={initialData?.actionOptions}
              isTextArea={false}
              style={{ height: '20px' }}
            />
          </Field>

          {/* Scope Selection */}
          {/* <Field>
            <Label>Scope</Label>
            <div className='flex gap-2'>
              {scopeOptions.map((option) => (
                <button
                  key={option.value}
                  type='button'
                  onClick={() => setSelectedScope(option.value as 'session' | 'persistent')}
                  className={clsx(
                    `h-fit flex items-center gap-2 px-4 py-2 rounded-sm border transition-all`,
                    selectedScope === option.value
                      ? 'bg-brand-600 border-brand-500 text-white'
                      : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                  )}
                >
                  <span>{option.label}</span>
                  {selectedScope === option.value && (
                    <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 24 24'>
                      <path d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z' />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </Field> */}
        </div>
      </div>

      {/* Action Buttons */}
      <div className='px-4 py-2 border-t bdr flex justify-between items-center mt-auto'>
        <Button variant={ButtonVariant.Outlined} onClick={onDismiss}>
          <Trans>Cancel</Trans>
        </Button>
        <Button onClick={handleSubmit} disabled={invalidForm}>
          <Trans>Save</Trans>
        </Button>
      </div>
    </div>
  )
}
